import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { teams } from '@/config/teams';
import { TeamProgress } from './TeamProgress';

type TeamBoardProps = {
  teamStates: Record<string, { level: number }>;
  submissionQueue: Array<{ teamId: string; validated?: boolean }>;
  teamColors: Record<number, {
    primary: string;
    secondary: string;
    text: string;
    hover: string;
  }>;
  onRollDice: (teamId: string) => void;
  isRolling: Record<string, boolean>;
  status: "connecting" | "connected" | "disconnected" | "error";
  teamDice: Record<string, { trigger: string; category: string }>;
  isFeminineMode: boolean;
  getTeamName: (team: any) => string;
};

export function TeamBoard({
  teamStates,
  submissionQueue,
  teamColors,
  onRollDice,
  isRolling,
  status,
  teamDice,
  isFeminineMode,
  getTeamName
}: TeamBoardProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Dice Display Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8 mb-8 lg:mb-12">
        {teams.map((team, index) => (
          <div key={team.id} className="space-y-3 lg:space-y-4">
            <h3 className="text-lg lg:text-xl font-semibold text-center">
              {getTeamName(team)}
            </h3>
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              {/* Trigger Dice */}
              <div className="relative">
                <div className={cn(
                  "h-16 lg:h-20 rounded-2xl shadow-lg flex flex-col items-center justify-center p-2 lg:p-3",
                  teamColors[index].primary
                )}>
                  <span className="text-xs lg:text-sm text-white font-bold text-center leading-tight overflow-hidden line-clamp-3">
                    {teamDice[team.id]?.trigger || "?"}
                  </span>
                </div>
                <h4 className="text-xs lg:text-sm font-semibold text-center mt-2">
                  {t('game.challenge.trigger')}
                </h4>
              </div>
              
              {/* Category Dice */}
              <div className="relative">
                <div className={cn(
                  "h-16 lg:h-20 rounded-2xl shadow-lg flex flex-col items-center justify-center p-2 lg:p-3",
                  teamColors[index].primary
                )}>
                  <span className="text-xs lg:text-sm text-white font-bold text-center leading-tight overflow-hidden line-clamp-3">
                    {teamDice[team.id]?.category || "?"}
                  </span>
                </div>
                <h4 className="text-xs lg:text-sm font-semibold text-center mt-2">
                  {t('game.challenge.category')}
                </h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Roll Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-3 lg:gap-4 mb-6 lg:mb-8">
        {teams.map((team, index) => {
          const hasSubmissionPending = submissionQueue?.some(
            (sub) => sub.teamId === team.id && !sub.validated
          );
          
          return (
            <Button
              key={team.id}
              onClick={() => onRollDice(team.id)}
              disabled={isRolling[team.id] || status !== "connected"}
              className={cn(
                "px-4 lg:px-6 py-2 lg:py-3 text-white transition-all duration-300 text-sm lg:text-base",
                isRolling[team.id] && "animate-shake",
                hasSubmissionPending ? teamColors[index].secondary : teamColors[index].primary,
                !hasSubmissionPending && teamColors[index].hover
              )}
            >
              {t('game.actions.rollDice')} {getTeamName(team)}
            </Button>
          );
        })}
      </div>

      {/* Team Progress */}
      <TeamProgress
        teams={teamStates}
        submissionQueue={submissionQueue}
        teamColors={teamColors}
        isFeminineMode={isFeminineMode}
        getTeamName={getTeamName}
      />
    </div>
  );
}
